// ─── Investigator cache: single source of truth for revalidation targets ────
//
// PURE + client-safe (no `next/cache`, no fetch, no server action here): this
// module only defines the cache "target catalog" (which tags/paths back each
// public surface), the descriptor type and its resolver. Split so there are no
// import cycles across the pieces:
//
//   investigator-profile-cache.ts   (this — pure catalog + resolver)
//     ├── investigator-revalidation.ts          (server-only executor)
//     │     └── investigator-revalidation-actions.ts  ('use server' action)
//     │           └── investigator-revalidation-client.ts (client wrappers)
//     └── (also imported directly by the two route handlers + investigadores-public)
//
// WHY a server action instead of a browser fetch to a route handler: in the
// pruebas/prod deploy, Apache proxies `/api/* -> NestJS`, so a browser POST to
// the Next `/api/investigadores/.../revalidate` route handler is hijacked by
// NestJS (404) and the cache never busts. A Server Action runs INSIDE Next
// (invoked via the page route, which Apache proxies to Next), so it is immune
// to that `/api/` collision. Traceability lives in
// `app/(dashboard)/admin/revalidaciones/registry.ts` (see AGENTS.md rule).

const INVESTIGATOR_PROFILE_CACHE_TAG_PREFIX = 'investigator-profile';
const MAX_INVESTIGATOR_PROFILE_SLUG_LENGTH = 235;

export const INVESTIGATOR_DIRECTORY_CACHE_TAG = 'investigator-directory';
/** `/sitemap.xml` (R12) also renders from the directory, but it is NOT listed
 * here on purpose: it is `force-dynamic`, so it has no route cache to bust — it
 * re-reads the tagged fetch above on every request and is therefore already
 * covered by the tag. Adding the path would only mean revalidating a route that
 * never caches.
 *
 * `/` SÍ hace falta: la portada volvió a leer el directorio (la vitrina "Conoce
 * a nuestros investigadores" y la línea de totales del banner), y cachea con
 * `revalidate = 3600`. Sin esta ruta, un cambio de perfil tardaría hasta una
 * hora en verse en el home aunque el directorio ya estuviera al día. */
export const INVESTIGATOR_DIRECTORY_REVALIDATION_PATHS = ['/', '/investigadores'] as const;
export const INVESTIGATOR_DIRECTORY_REVALIDATION_TAGS = [INVESTIGATOR_DIRECTORY_CACHE_TAG] as const;

/** Legacy owner routes. They no longer render a profile: all three are now pure
 * redirects to the public `/investigadores/[slug]` equivalent (the owner's view
 * is layered on the public page in the client). They render with `revalidate=0`,
 * so revalidating them is a safe no-op — kept in the catalog because the
 * descriptor is still accepted by both revalidate route handlers, and so any
 * future cache addition here is covered without hunting call sites. The owner's
 * data itself is covered by the `profiles` target. */
export const MI_PERFIL_REVALIDATION_PATHS = ['/mi-perfil', '/mi-perfil/editar', '/mi-perfil/exportar'] as const;

const INVESTIGATOR_SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i;

export function isValidInvestigatorProfileSlug(slug: string) {
	const value = slug.trim();
	return value.length > 0 && value.length <= MAX_INVESTIGATOR_PROFILE_SLUG_LENGTH && INVESTIGATOR_SLUG_REGEX.test(value);
}

export function getInvestigatorProfileCacheTag(slug: string) {
	return `${INVESTIGATOR_PROFILE_CACHE_TAG_PREFIX}:${slug.trim().toLowerCase()}`;
}

/**
 * A rename reuses the existing `public_uid` suffix, so old and new slug share
 * their LAST segment. Only a previous slug that shares the suffix may be
 * revalidated alongside the new one — this stops a caller from busting an
 * arbitrary unrelated profile's cache through the `previousSlug` field.
 */
export function hasSameGeneratedSuffix(slug: string, previousSlug: string) {
	const finalSegment = (value: string) => {
		const segments = value.split('-');
		return segments[segments.length - 1] ?? '';
	};
	return finalSegment(slug) === finalSegment(previousSlug);
}

// ─── Target catalog + descriptor resolver ──────────────────────────────────

export interface InvestigatorRevalidationTargets {
	tags: string[];
	paths: string[];
}

/**
 * Declarative description of WHAT to revalidate. A single mutation can fan out
 * to several surfaces — e.g. a name change busts the new profile, the previous
 * slug's profile AND the directory listing, so the old link disappears from the
 * listing instead of lingering.
 */
export interface InvestigatorRevalidationDescriptor {
	/** Public directory + home listing (`/`, `/investigadores`). */
	directory?: boolean;
	/** One or more profile pages by slug (public + export + edit). */
	profiles?: string[];
	/** Legacy `/mi-perfil*` routes — now redirects, so this is a no-op. */
	miPerfil?: boolean;
	/**
	 * EVERY public profile at once (R67). For changes whose affected slugs nobody knows, like a
	 * bibliometric import run: the Python runner writes publications for many authors. Only the
	 * collection route honours it, and only with the deploy secret (it is a mass bust).
	 */
	allProfiles?: boolean;
}

/**
 * Route PATTERN that covers every profile page. A path with a `[segment]` is revalidated as a
 * `layout` by the executor, which invalidates every page under it — including the per-slug
 * data fetched while rendering them.
 */
export const ALL_INVESTIGATOR_PROFILES_PATH = '/investigadores/[slug]';

/** True for a path that is a route pattern (`[segment]`) instead of a concrete URL. */
export function isRoutePatternPath(path: string) {
	return /\[[^/\]]+\]/.test(path);
}

export function getInvestigatorDirectoryTargets(): InvestigatorRevalidationTargets {
	return {
		tags: [...INVESTIGATOR_DIRECTORY_REVALIDATION_TAGS],
		paths: [...INVESTIGATOR_DIRECTORY_REVALIDATION_PATHS],
	};
}

export function getInvestigatorProfileTargets(slug: string): InvestigatorRevalidationTargets {
	const normalized = slug.trim().toLowerCase();
	const encoded = encodeURIComponent(normalized);
	return {
		tags: [getInvestigatorProfileCacheTag(normalized)],
		paths: [
			`/investigadores/${encoded}`,
			`/investigadores/${encoded}/exportar`,
			`/investigadores/${encoded}/editar`,
		],
	};
}

export function getMiPerfilTargets(): InvestigatorRevalidationTargets {
	return { tags: [], paths: [...MI_PERFIL_REVALIDATION_PATHS] };
}

/**
 * Map a descriptor to the concrete, de-duplicated set of tags + paths.
 * Pure, so both the client (for reporting) and the server executor share the
 * exact same mapping. Invalid slugs are dropped silently.
 */
export function resolveInvestigatorRevalidationTargets(
	descriptor: InvestigatorRevalidationDescriptor,
): InvestigatorRevalidationTargets {
	const tags = new Set<string>();
	const paths = new Set<string>();

	const add = (target: InvestigatorRevalidationTargets) => {
		target.tags.forEach((tag) => tags.add(tag));
		target.paths.forEach((path) => paths.add(path));
	};

	if (descriptor.directory) add(getInvestigatorDirectoryTargets());

	for (const slug of descriptor.profiles ?? []) {
		if (isValidInvestigatorProfileSlug(slug)) add(getInvestigatorProfileTargets(slug));
	}

	if (descriptor.miPerfil) add(getMiPerfilTargets());

	if (descriptor.allProfiles) add({ tags: [], paths: [ALL_INVESTIGATOR_PROFILES_PATH] });

	return { tags: [...tags], paths: [...paths] };
}
